import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { TimeInputContainer, TimeInputField } from './styles';

interface TimeInputProps {
  value: string;
  onChange: (time: string) => void;
  minuteStep?: number;
  timeFormat?: '12h' | '24h';
}

/**
 * Convert 24h HH:mm:ss to 12h hh:mm:ss AM/PM for display
 */
const to12h = (time24: string): string => {
  const parts = time24.split(':');
  let hours = parseInt(parts[0], 10) || 0;
  const minutes = parts[1] ?? '00';
  const seconds = parts[2] ?? '00';
  const period = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;
  return `${hours.toString().padStart(2, '0')}:${minutes}:${seconds} ${period}`;
};

/**
 * Convert 12h hh:mm:ss AM/PM back to 24h HH:mm:ss
 */
const to24h = (time12: string): string | null => {
  const match = time12.trim().match(/^(0?[1-9]|1[0-2]):([0-5]\d):([0-5]\d)\s*(AM|PM)$/i);
  if (!match) return null;
  let hours = parseInt(match[1], 10);
  const minutes = match[2];
  const seconds = match[3];
  const period = match[4].toUpperCase();
  if (period === 'AM' && hours === 12) hours = 0;
  if (period === 'PM' && hours !== 12) hours += 12;
  return `${hours.toString().padStart(2, '0')}:${minutes}:${seconds}`;
};

export const TimeInput: React.FC<TimeInputProps> = ({
  value,
  onChange,
  minuteStep = 5,
  timeFormat = '24h',
}) => {
  const displayValue = useMemo(
    () => (timeFormat === '12h' ? to12h(value) : value),
    [value, timeFormat],
  );
  const [inputValue, setInputValue] = useState(displayValue);

  useEffect(() => {
    setInputValue(displayValue);
  }, [displayValue]);

  const handleBlur = useCallback(() => {
    // Parse back to 24h for the onChange callback
    let hours: number;
    let minutes: number;
    let seconds: number;

    if (timeFormat === '12h') {
      const converted = to24h(inputValue);
      if (!converted) {
        setInputValue(displayValue);
        return;
      }
      const parts = converted.split(':');
      hours = parseInt(parts[0], 10);
      minutes = parseInt(parts[1], 10);
      seconds = parseInt(parts[2], 10);
    } else {
      const timeParts = inputValue.split(':');
      hours = parseInt(timeParts[0], 10);
      minutes = parseInt(timeParts[1], 10);
      seconds = timeParts[2] ? parseInt(timeParts[2], 10) : 0;
    }

    if (isNaN(hours) || isNaN(minutes)) {
      setInputValue(displayValue);
      return;
    }

    const clampedHours = Math.max(0, Math.min(23, hours));
    let finalMinutes = minutes;
    if (minuteStep > 1) {
      finalMinutes = Math.round(minutes / minuteStep) * minuteStep;
    }
    const clampedMinutes = Math.max(0, Math.min(59, finalMinutes));
    const clampedSeconds = Math.max(0, Math.min(59, seconds));

    // Always emit 24h format
    const snappedTime = `${clampedHours.toString().padStart(2, '0')}:${clampedMinutes
      .toString()
      .padStart(2, '0')}:${clampedSeconds.toString().padStart(2, '0')}`;

    // Update display in the correct format
    setInputValue(timeFormat === '12h' ? to12h(snappedTime) : snappedTime);
    onChange(snappedTime);
  }, [inputValue, minuteStep, onChange, displayValue, timeFormat]);

  const handleChange = useCallback((e: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setInputValue((e.target as HTMLInputElement).value);
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      e.stopPropagation();
      if (e.key === 'Enter') {
        e.currentTarget.blur();
      }
    },
    [],
  );

  return (
    <TimeInputContainer>
      <TimeInputField
        type="text"
        value={inputValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        placeholder={timeFormat === '12h' ? '12:00:00 AM' : '00:00:00'}
      />
    </TimeInputContainer>
  );
};
